import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import * as Sentry from '@sentry/react';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Row } from 'apps/auth/components/common/Layout/Row';
import ComplexPopup from 'apps/auth/components/common/Popup/ComplexPopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import Form from 'apps/auth/components/form-multi/Form';
import NewInputWrapper from 'apps/auth/components/form-multi/NewInputWrapper';
import { AMOUNT_FROM, AMOUNT_TO, AMOUNT_TO_CCY } from 'apps/auth/constants/fieldsName';
import { GA_SEND_FORM_NAME } from 'apps/auth/constants/formName';
import { logInfo } from 'apps/auth/helpers/clientLogger';
import { resolveArguments } from 'apps/auth/helpers/utils';

const numView = (num) => (Math.round(Number(num) * 100) / 100).toFixed(2);

const renderRemoteAvatar = (url) => <DiskBadge src={url} alt="User" flagName="paysend" diskSize="large" />;

const renderDefaultAvatar = (name) => {
    if (!!name && name.length > 0) {
        const firstLetter = name[0];
        return <DiskBadge diskText={firstLetter.toUpperCase()} flagName="paysend" diskSize="large" />;
    }

    return null;
};

function useCheckingUrlAvailability(url) {
    const [isAvailable, setAvailable] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!!url && url.length) {
                    const resp = await fetch(url, { method: 'GET' });
                    setAvailable(resp.status === 200);
                }
            } catch (e) {
                Sentry.captureException(e, {
                    level: 'log',
                });
                setAvailable(false);
            }
        };

        fetchData();
    }, [url]);

    return isAvailable;
}

const SendCommentPopup = (props) => {
    const submitRef = useRef(null);
    const iaAvailable = useCheckingUrlAvailability(props.avatarUrl);

    const [comment, setComment] = useState(props.comment || '');

    const renderAvatar = () => {
        return props.avatarUrl && iaAvailable ? renderRemoteAvatar(props.avatarUrl) : renderDefaultAvatar(props.firstName);
    };

    const getCurrencyTo = () => {
        const currency = props.currenciesTo.find((currency) => Number(currency.id) === Number(props.amountToCurrency));
        return currency ? currency.code : '';
    };

    const renderTransactionInfo = () => (
        <>
            <Row indent="adaptive-16-24">{renderAvatar()}</Row>
            <Text variant="div" indent="4px">
                {resolveArguments(props.toRecipient, props.firstName, props.secondName)}
            </Text>
            <Text variant="div" size="display2" indent="adaptive-32-48">
                {numView(props.amountFromValue)}
                <Text size="display2" smallCaps leftSpacer>
                    &nbsp;{props.amountFromCurrency.toLowerCase()}
                </Text>
                {' → '}
                {numView(props.amountToValue)}
                <Text size="display2" smallCaps leftSpacer>
                    {getCurrencyTo().toLowerCase()}
                </Text>
            </Text>
        </>
    );

    const getP2PAction = () => `/auth/ga/pocket/${props.pocketId}/p2p/pay.create?comment=${comment}`;

    const focus = (e) => {
        if (e.currentTarget) {
            const input = e.currentTarget.firstElementChild;
            input.focus();
        } else {
            logInfo('Focus method not support');
        }
    };

    return (
        <ComplexPopup
            popupRef={props.popupRef}
            textCenter
            primaryButtonType="submit"
            primaryButtonText={props.confirmAndSend}
            primaryButtonForm="send_comment"
            formRef={submitRef}
        >
            {renderTransactionInfo()}
            <Form submitRef={submitRef} formName={props.formName} action={getP2PAction()} validateForm={false} getCanonicalPath={true} id="send_comment">
                <NewInputWrapper name="optional" setFocus={focus} isCenteredInput>
                    <input type="text" value={comment} placeholder={props.commentPlaceholder} onChange={(e) => setComment(e.target.value)} />
                </NewInputWrapper>
            </Form>
        </ComplexPopup>
    );
};

const mapStateToProps = ({ extra, form, messages }) => ({
    pocketId: extra.pocket?.id,
    avatarUrl: extra.avatar?.url,
    firstName: extra.firstName,
    secondName: extra.secondName,
    amountFromValue: form[GA_SEND_FORM_NAME].fields[AMOUNT_FROM].value,
    amountToValue: form[GA_SEND_FORM_NAME].fields[AMOUNT_TO].value,
    amountToCurrency: form[GA_SEND_FORM_NAME].fields[AMOUNT_TO_CCY].value,
    amountFromCurrency: extra.commission.restrictionFrom.ccyIso,
    confirmAndSend: messages.pageMessages.confirmAndSend,
    commentPlaceholder: messages.pageMessages.commentPlaceholder,
    toRecipient: messages.pageMessages.toRecipient,
    currenciesTo: extra.toCountry.currencies,
    comment: extra.comment,
});

export default connect(mapStateToProps, null)(SendCommentPopup);
